import React, { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { format, parseISO } from "date-fns";
import { Loader2, DollarSign, Users, Wallet, CheckCircle2, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  const load = () => api.get("/employee/dashboard").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  if (!data) {
    return (
      <div className="grain min-h-screen bg-background">
        <Header onSchedule={() => {}} />
        <div className="flex h-[60vh] items-center justify-center text-zinc-500">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </div>
    );
  }

  const stat = (label, value, Icon, accent) => (
    <div className="border border-white/10 bg-[#0A0A0B] p-6">
      <div className={`flex h-10 w-10 items-center justify-center border border-white/10 bg-white/[0.03] ${accent}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">{label}</p>
      <p className="mt-1 font-display text-3xl font-bold">{value}</p>
    </div>
  );

  return (
    <div className="grain min-h-screen bg-background text-white">
      <Header onSchedule={() => {}} />
      <div className="mx-auto max-w-7xl px-6 py-8">
        <div className="mb-6">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">// Referral Partner</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">{user?.name}</h1>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {stat("Total Earned", `$${data.earned}`, DollarSign, "text-emerald-400")}
          {stat("Paid Out", `$${data.total_paid}`, Wallet, "text-primary")}
          {stat("Balance Owed", `$${data.balance}`, Clock, "text-amber-400")}
        </div>

        <p className="mt-4 font-mono text-xs text-zinc-500">
          You earn <span className="text-primary">${data.commission_per_client}</span> for each client you refer once they pay their deposit.
        </p>

        {/* Referred clients */}
        <div className="mt-8 border border-white/10 bg-[#0A0A0B]">
          <div className="flex items-center gap-2 border-b border-white/10 px-6 py-4">
            <Users className="h-4 w-4 text-primary" />
            <h2 className="font-display font-semibold">People you referred</h2>
            <Badge className="ml-2 rounded-sm bg-white/5 text-[10px] text-zinc-300">{data.referred_count}</Badge>
          </div>
          <div className="grid grid-cols-12 border-b border-white/10 px-6 py-3 font-mono text-[11px] uppercase tracking-wider text-zinc-500">
            <div className="col-span-4">Client</div>
            <div className="col-span-3">Referred on</div>
            <div className="col-span-2">Deposit</div>
            <div className="col-span-3 text-right">Commission</div>
          </div>
          {data.referrals.length === 0 && <p className="p-6 text-sm text-zinc-500">No clients assigned to you yet.</p>}
          {data.referrals.map((r) => (
            <div key={r.project_id} data-testid={`referral-${r.project_id}`}
              className="grid grid-cols-12 items-center border-b border-white/5 px-6 py-4 text-sm">
              <div className="col-span-4 overflow-hidden">
                <p className="truncate font-medium">{r.client_name}</p>
                <p className="truncate font-mono text-[11px] text-zinc-500">{r.business_name || "—"}</p>
              </div>
              <div className="col-span-3 font-mono text-[12px] text-zinc-400">
                {r.assigned_at ? format(parseISO(r.assigned_at), "MMM d, yyyy") : "—"}
              </div>
              <div className="col-span-2">
                {r.deposit_paid ? (
                  <Badge className="rounded-sm bg-emerald-500/15 text-[10px] text-emerald-400">
                    <CheckCircle2 className="mr-1 h-3 w-3" /> Paid
                  </Badge>
                ) : (
                  <Badge className="rounded-sm bg-white/5 text-[10px] text-zinc-400">Pending</Badge>
                )}
              </div>
              <div className={`col-span-3 text-right font-mono ${r.commission ? "text-emerald-400" : "text-zinc-600"}`}>
                ${r.commission}
              </div>
            </div>
          ))}
        </div>

        {/* Payout history */}
        <div className="mt-6 border border-white/10 bg-[#0A0A0B]">
          <div className="flex items-center gap-2 border-b border-white/10 px-6 py-4">
            <Wallet className="h-4 w-4 text-primary" />
            <h2 className="font-display font-semibold">Payment history</h2>
          </div>
          {data.payouts.length === 0 && <p className="p-6 text-sm text-zinc-500">No payments recorded yet.</p>}
          {data.payouts.map((p) => (
            <div key={p.id} data-testid={`payout-${p.id}`}
              className="flex items-center justify-between border-b border-white/5 px-6 py-4 text-sm">
              <div>
                <p className="font-mono text-emerald-400">+ ${p.amount}</p>
                {p.note && <p className="text-xs text-zinc-500">{p.note}</p>}
              </div>
              <p className="font-mono text-[12px] text-zinc-500">
                {p.paid_at ? format(parseISO(p.paid_at), "MMM d, yyyy · h:mm a") : ""}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
